import React from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  Grid,
  Tooltip,
} from "@mui/material";
import "./service-offerings.scss";

interface ServicesOfferingsProps {
  data: any;
  refresh?: () => void;
}

const ServicesOfferings: React.FC<ServicesOfferingsProps> = ({ data }) => {
  const formatNeedType = (type: string) => {
    if (type === "At Need" || type === "at_need") return "Immediate Need";
    if (type === "Pre Need" || type === "pre_need") return "Preneed";
    return type;
  };

  const needTypesValue = Array.isArray(data?.needTypes)
    ? data.needTypes.map(formatNeedType).join(", ")
    : data?.needTypes
    ? formatNeedType(data.needTypes)
    : "-";

  const serviceCategories = data?.serviceCategories || [];
  const services = data?.services || {};

  return (
    <Box className="services-offerings">
      {/* Heading */}
      <Typography variant="h6" className="services-offerings__title">
        Service Offerings
      </Typography>

      <Typography variant="body2" className="services-offerings__subtitle">
         View all the service categories and services you offer in one place.
      </Typography>

     {/* Need Type */}
      <Box className="services-offerings__select">
        <Typography className="services-offerings__select-text">
          Need Type
        </Typography>

        {Array.isArray(data?.needTypes) && data.needTypes.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mt: 1,
            }}
          >
            {data.needTypes.map((type: string) => (
              <Chip
                key={type}
                label={formatNeedType(type)}
                className="services-offerings__chip"
              />
            ))}
          </Box>
        ) : data?.needTypes ? (
          <Chip
            label={formatNeedType(data.needTypes)}
            className="services-offerings__chip"
          />
        ) : (
          <Typography
            variant="body2"
            className="services-offerings__empty-text"
          >
            No need type available.
          </Typography>
        )}
      </Box>

      {/* Service Categories */}
      <Box className="services-offerings__select">
        <Typography className="services-offerings__select-text">
          Service Categories
        </Typography>

        {serviceCategories.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mt: 1,
            }}
          >
            {serviceCategories.map((category: string) => (
              <Chip
                key={category}
                label={category}
                className="services-offerings__chip"
              />
            ))}
          </Box>
        ) : (
          <Box className="services-offerings__empty">
            <Typography
              variant="body2"
              className="services-offerings__empty-text"
            >
              No service categories available.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Subcategories Section */}
      {serviceCategories.length === 0 ? (
        <Box className="services-offerings__empty">
          <Typography
            variant="body2"
            className="services-offerings__empty-text"
          >
            No services available.
          </Typography>
        </Box>
      ) : (
        <Box className="services-offerings__subcategories">
          <Typography className="services-offerings__subcategories-text">
            List of Services
          </Typography>

          {serviceCategories.map((category: string) => {
            const subcategories = services?.[category] || [];

            return (
              <Box
                key={category}
                className="services-offerings__category-box"
              >
                <Typography
                  variant="subtitle1"
                  className="services-offerings__category-title"
                >
                  {category}
                </Typography>

                {subcategories.length > 0 ? (
                  <Grid container spacing={2}>
                    {subcategories.map((service: any) => (
                      <Grid item xs={12} sm={6} md={3} key={service.serviceId}>
                        <Box className="services-offerings__subcategory">
                          <Tooltip
                            title={service.serviceName}
                            arrow
                            placement="top"
                            disableHoverListener={
                              !service?.serviceName ||
                              service.serviceName.length < 25
                            }
                          >
                            <Typography
                              variant="body2"
                              className="services-offerings__service-name-text"
                              noWrap
                            >
                              {service?.serviceName || "-"}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography
                    variant="body2"
                    className="services-offerings__empty-text"
                  >
                    No list of services for this category.
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ServicesOfferings;